import { ApplicationSetting } from '@app/core/model/enums/application-setting.enum'
import { ApplicationSettingCategory } from './application-setting-category.enum'
import { ApplicationSettingConfig } from '../models/application-settings-item.model'

export const ApplicationSettingsConfig: Record<string, ApplicationSettingConfig> = {
  /* #region SCOPE */
  [ApplicationSetting.SCOPE__START_END_DATE]: {
    name: 'Show {LANG:scope} date',
    category: ApplicationSettingCategory.SCOPE,
    description: 'Allows users to set up and display scope start and end date',
    order: 0,
  },
  [ApplicationSetting.SCOPE__APPROVAL__STATUS_CLIENT]: {
    name: 'Enable Client Approval {LANG:scope} Status',
    category: ApplicationSettingCategory.SCOPE,
    description: 'Adds a status to the Approval flow where the client approves the scope',
    order: 1,
  },
  [ApplicationSetting.SCOPE__DELIVERABLE__COMPONENT_PRICE]: {
    name: 'Show price in {LANG:component.p} panel',
    category: ApplicationSettingCategory.SCOPE,
    description:
      'Allows to hide the pricing information within the {LANG:component.p} panel of the {LANG:deliverable} builder',
    hidden: true,
    order: 2,
  },
  [ApplicationSetting.SCOPE__FIXED_COST_DELIVERABLE_EDITING]: {
    name: 'Enable Fixed Cost {LANG:deliverable} and {LANG:component} Editing',
    category: ApplicationSettingCategory.SCOPE,
    description: 'Allows users to create and edit fixed cost {LANG:deliverable.p} and {LANG:component.p}',
    hidden: true,
    order: 3,
  },
  [ApplicationSetting.SCOPE__DETAILS]: {
    name: 'Show {LANG:scope} Details',
    category: ApplicationSettingCategory.SCOPE,
    description: 'Allows users to create and edit fixed cost {LANG:deliverable.p} and {LANG:component.p}',
    hidden: true,
    order: 4,
  },
  [ApplicationSetting.PREDICT__SCOPEMARK__TPCS]: {
    name: 'Display ScopeMark TPCs',
    description: 'Displays out of the box third party costs (demo) items in Library',
    category: ApplicationSettingCategory.SCOPE,
    isConfigurable: true,
    config: (user) => isNotLegalCompanyTypeOrEnabled(user.company, ApplicationSetting.PREDICT__SCOPEMARK__TPCS),
    order: 5,
  },
  [ApplicationSetting.PREDICT__SCOPEMARK__DISCIPLINES]: {
    name: 'Display ScopeMark Disciplines',
    description:
      'Allows users to view and use out of the box (demo) disciplines (departments or expertise areas) when building new {LANG:scope.p} or library objects ({LANG:deliverable.p}, {LANG:component.p})',
    category: ApplicationSettingCategory.SCOPE,
    isConfigurable: true,
    config: (user) => isNotLegalCompanyTypeOrEnabled(user.company, ApplicationSetting.PREDICT__SCOPEMARK__DISCIPLINES),
    order: 6,
  },
  [ApplicationSetting.PREDICT__SCOPEMARK__DELIVERABLES]: {
    name: 'Display {LANG:scope_mark} {LANG:deliverable.p}',
    category: ApplicationSettingCategory.SCOPE,
    description:
      'Shows predefined out of the box templated {LANG:deliverable.p} linked to ScopeMark disciplines and ratecard',
    isConfigurable: true,
    config: (user) => isNotLegalCompanyTypeOrEnabled(user.company, ApplicationSetting.PREDICT__SCOPEMARK__DELIVERABLES),
    order: 7,
  },
  [ApplicationSetting.PREDICT__SCOPEMARK__COMPONENTS]: {
    name: 'Display {LANG:scope_mark} {LANG:component.p}',
    category: ApplicationSettingCategory.SCOPE,
    description:
      'Shows predefined out of the box templated {LANG:component.p} linked to ScopeMark disciplines and ratecard',
    isConfigurable: true,
    config: (user) => isNotLegalCompanyTypeOrEnabled(user.company, ApplicationSetting.PREDICT__SCOPEMARK__COMPONENTS),
    order: 8,
  },
  [ApplicationSetting.PREDICT__COMPANY_SCOPEMARK__COMPONENTS]: {
    name: 'Display {LANG:scope_mark} Overridden {LANG:component.p}',
    hidden: true,
    category: ApplicationSettingCategory.SCOPE,
    order: 9,
  },
  [ApplicationSetting.PREDICT__LIBRARY__COMPONENTS]: {
    name: 'Predict Library {LANG:component.p}',
    category: ApplicationSettingCategory.SCOPE,
    hidden: true,
    order: 10,
  },
  [ApplicationSetting.PREDICT__LIBRARY__DELIVERABLES]: {
    name: 'Predict Library {LANG:deliverable}',
    category: ApplicationSettingCategory.SCOPE,
    hidden: true,
    order: 11,
  },
  [ApplicationSetting.SCOPE__BREAKDOWN]: {
    name: 'Show {LANG:scope} Breakdown view',
    category: ApplicationSettingCategory.SCOPE,
    description: 'Allows to hide or display Breakdown tab on every {LANG:scope}',
    hidden: true,
    order: 12,
  },
  [ApplicationSetting.SCOPE__OUTPUT]: {
    name: 'Show {LANG:scope} {LANG:output} view',
    category: ApplicationSettingCategory.SCOPE,
    description: 'Allows to hide or display Output tab on every {LANG:scope}',
    hidden: true,
    order: 13,
  },
  [ApplicationSetting.SCOPE__TYPE_VIEW]: {
    name: 'Show {LANG:scope} types',
    category: ApplicationSettingCategory.SCOPE,
    description:
      'Displays Scope Types page under Manage section aand allows to categorize {LANG:scope.p} by type (e.g., creative, media, production).',
    order: 14,
  },
  [ApplicationSetting.SCOPE__AGENCY_RATES]: {
    name: 'View Rates',
    description: 'Enables users to see rate cards or hourly rates within the scope.',
    category: ApplicationSettingCategory.SCOPE,
    order: 15,
  },
  [ApplicationSetting.SCOPE__AGENCY_HOURS]: {
    name: 'View {LANG:agency} Hours',
    category: ApplicationSettingCategory.SCOPE,
    hidden: true,
    order: 16,
  },
  [ApplicationSetting.SCOPE__SHOW_COLLABORATORS_THUMBNAIL]: {
    name: 'View collaborators in {LANG:scope} overview',
    category: ApplicationSettingCategory.SCOPE,
    hidden: true,
    order: 17,
  },
  [ApplicationSetting.SCOPE_OUTPUT_DOCX__BLANK_TEMPLATE]: {
    name: '{LANG:scope} {LANG:output} Docx Blank',
    category: ApplicationSettingCategory.SCOPE,
    hidden: true,
    order: 18,
  },
  [ApplicationSetting.SCOPE__BENCHMARK_RATE_CARD]: {
    name: "Show 'Benchmark Rate Card' select item in 'Create {LANG:scope}' form",
    category: ApplicationSettingCategory.SCOPE,
    description:
      'Adds a selectable benchmark rate card when creating a new {LANG:scope} and the comparison is shown in the designated output spreadsheet',
    order: 19,
  },
  [ApplicationSetting.SCOPE__ENABLE_FEES]: {
    name: '{LANG:scope} Fees',
    category: ApplicationSettingCategory.SCOPE,
    description: 'Controls whether fees at the {LANG:scope} level are shown and editable.',
    dependOnSetting: ApplicationSetting.FEE_ITEM__ENABLE,
    order: 20,
  },
  [ApplicationSetting.SCOPE__ENABLE_SECTION_FEES]: {
    name: 'Stage Fees',
    category: ApplicationSettingCategory.SCOPE,
    description: 'Controls whether fees at the stage level are shown and editable',
    dependOnSetting: ApplicationSetting.FEE_ITEM__ENABLE,
    order: 21,
  },
  [ApplicationSetting.SCOPE_BY_ROLE__ENABLE]: {
    name: '{LANG:scope} by Role',
    category: ApplicationSettingCategory.SCOPE,
    description:
      'Allows alternative scoping work based on user roles instead of {LANG:component.p} and {LANG:deliverable.p}.',
    order: 22,
  },
  [ApplicationSetting.SCOPE_BY_SCENARIO]: {
    name: 'Scenario Pricing',
    category: ApplicationSettingCategory.SCOPE,
    hidden: true,
    order: 23,
  },
  [ApplicationSetting.SCOPE__ENABLE_SCENARIO_PRICING]: {
    name: 'Scenario Pricing',
    category: ApplicationSettingCategory.SCOPE,
    dependOnSetting: ApplicationSetting.SCOPE_BY_SCENARIO,
    order: 24,
  },
  [ApplicationSetting.TIMELINE_VIEW__ENABLE]: {
    name: 'Timeline View',
    description:
      'Allows to view {LANG:scope.p} as Gantt charts. {LANG:scope} start and end dates should be also enabled for this feature to work',
    category: ApplicationSettingCategory.SCOPE,
    order: 25,
  },
  [ApplicationSetting.COST_PLUS]: {
    name: 'Cost Plus',
    description: 'Enables cost-plus pricing model',
    category: ApplicationSettingCategory.SCOPE,
    hidden: true,
    order: 26,
  },
  [ApplicationSetting.SCOPE_DYNAMIC_FIELDS]: {
    name: 'Scope Dynamic Fields',
    description:
      'Allows adding custom fields to {LANG:scope} records as text, dropdown lists or used in integrations with 3rd party systems to pull external dropdown lists (e.g. project managers, job numbers)',
    category: ApplicationSettingCategory.SCOPE,
    hidden: false,
    order: 27,
  },
  [ApplicationSetting.DELIVERABLE_DYNAMIC_FIELDS]: {
    name: 'Deliverable Dynamic Fields',
    description:
      'Allows adding custom fields to {LANG:deliverable} records as text, dropdown lists or used in integrations with 3rd party systems to pull external dropdown lists (e.g. project managers, job numbers)',
    category: ApplicationSettingCategory.SCOPE,
    hidden: false,
    order: 28,
  },
  [ApplicationSetting.SCOPE__EXTRA_DESCRIPTION_FIELD]: {
    name: 'Enable Extra Description field',
    category: ApplicationSettingCategory.SCOPE,
    order: 29,
  },
  [ApplicationSetting.CHANGE_REQUEST]: {
    name: 'Change Request',
    category: ApplicationSettingCategory.SCOPE,
    hidden: true,
    order: 30,
  },
  [ApplicationSetting.SCOPE__ENABLE_CHANGE_REQUEST]: {
    name: 'Change Request',
    category: ApplicationSettingCategory.SCOPE,
    dependOnSetting: ApplicationSetting.CHANGE_REQUEST,
    order: 31,
  },
  /* #endregion */

  /* #region DELIVERABLE */
  [ApplicationSetting.SCOPE__DELIVERABLE__STARTEND_DATE]: {
    name: 'Start & End Dates',
    category: ApplicationSettingCategory.DELIVERABLE,
    description: 'Allows users to set up and display {LANG:deliverable.p} start and end date',
    order: 0,
  },
  [ApplicationSetting.SCOPE__DELIVERABLE__COMPULSORY__STARTEND_DATE]: {
    name: 'Compulsory Start & End Dates',
    category: ApplicationSettingCategory.DELIVERABLE,
    dependOnSetting: ApplicationSetting.SCOPE__DELIVERABLE__STARTEND_DATE,
    order: 1,
  },
  [ApplicationSetting.SCOPE__MARGIN_SHOW]: {
    name: '{LANG:deliverable} Margin',
    category: ApplicationSettingCategory.DELIVERABLE,
    description:
      'Allows users to view {LANG:deliverable} margin (calculated as a % of the selling price and displayed in monetary value)',
    order: 2,
  },
  [ApplicationSetting.SCOPE__MARKUP_SHOW]: {
    name: '{LANG:deliverable} Markup',
    category: ApplicationSettingCategory.DELIVERABLE,
    description: 'Allows users to view the markup (calculated as % of the cost price and displayed as %)',
    order: 3,
  },
  [ApplicationSetting.SCOPE__DELIVERABLE__TRADING]: {
    name: 'Enable {LANG:deliverable} Trading',
    category: ApplicationSettingCategory.DELIVERABLE,
    description: 'Allows users to replace deliverables after Client approval',
    hidden: true,
    order: 4,
  },
  [ApplicationSetting.SCOPE__DELIVERABLE__OWNER]: {
    name: '{LANG:deliverable} Owner',
    category: ApplicationSettingCategory.DELIVERABLE,
    hidden: true,
    order: 5,
  },
  [ApplicationSetting.SCOPE__AGENCY_HOURS_AND_MINUTES]: {
    name: 'Enable {LANG:agency} Hours with Minutes',
    category: ApplicationSettingCategory.DELIVERABLE,
    description: 'Allows users to make the start and end dates compulsory (important for some integrations)',
    order: 6,
  },
  [ApplicationSetting.DELIVERABLE__ENABLE_FEES]: {
    name: 'Deliverable Fees',
    description: 'Controls whether fees at the deliverable level are shown and editable',
    category: ApplicationSettingCategory.DELIVERABLE,
    dependOnSetting: ApplicationSetting.FEE_ITEM__ENABLE,
    order: 7,
  },
  [ApplicationSetting.COMPONENT__ENABLE_FEES]: {
    name: 'Component Fees',
    description: 'Controls whether fees at the component level are shown and editable',
    category: ApplicationSettingCategory.DELIVERABLE,
    dependOnSetting: ApplicationSetting.FEE_ITEM__ENABLE,
    order: 8,
  },
  /* #endregion */

  /* #region DOCUSIGN */
  [ApplicationSetting.DOCUSIGN_OUTPUT_ON_DRAFT]: {
    name: 'Enable DocuSign on Draft',
    category: ApplicationSettingCategory.DOCUSIGN,
    order: 0,
  },
  [ApplicationSetting.DOCUSIGN_OUTPUT_ON_REVIEW]: {
    name: 'Enable DocuSign on In Review',
    category: ApplicationSettingCategory.DOCUSIGN,
    order: 1,
  },
  [ApplicationSetting.DOCUSIGN_OUTPUT_ON_AGREEMENT]: {
    name: 'Enable DocuSign on In Approval',
    category: ApplicationSettingCategory.DOCUSIGN,
    order: 2,
  },
  [ApplicationSetting.DOCUSIGN_OUTPUT_ON_CLIENT_APPROVAL]: {
    name: 'Enable DocuSign on In Client Approval',
    category: ApplicationSettingCategory.DOCUSIGN,
    order: 3,
  },
  [ApplicationSetting.DOCUSIGN_OUTPUT_ON_CLIENT_ACCEPTED]: {
    name: 'Enable DocuSign on Client Accepted',
    category: ApplicationSettingCategory.DOCUSIGN,
    order: 4,
  },
  [ApplicationSetting.DOCUSING_OUTPUT_ON_CLOSED]: {
    name: 'Enable DocuSign on Closed',
    category: ApplicationSettingCategory.DOCUSIGN,
    order: 5,
  },
  /* #endregion */

  /* #region TRAFFICKING */
  [ApplicationSetting.SCOPE__TRAFFICKING__DELIVERABLE_ACTUALS]: {
    name: 'Show Actuals',
    category: ApplicationSettingCategory.TRAFFICKING,
    order: 0,
  },
  [ApplicationSetting.TRAFFICKABLE_ON_STATUS__DRAFT]: {
    name: 'Traffic on Draft Status',
    category: ApplicationSettingCategory.TRAFFICKING,
    order: 1,
  },
  [ApplicationSetting.TRAFFICKABLE_ON_STATUS__REVIEW]: {
    name: 'Traffic on In Review Status',
    category: ApplicationSettingCategory.TRAFFICKING,
    order: 2,
  },
  [ApplicationSetting.TRAFFICKABLE_ON_STATUS__AGREE]: {
    name: 'Traffic on In Approval Status',
    category: ApplicationSettingCategory.TRAFFICKING,
    order: 3,
  },
  [ApplicationSetting.TRAFFIC__IMPORTS_AUTO_SYNC]: {
    name: 'Enable Auto Sync for Imported Clients & Ratecards',
    category: ApplicationSettingCategory.TRAFFICKING,
    hidden: true,
    order: 4,
  },
  [ApplicationSetting.INTEGRATION_RESOURCE_MANAGEMENT]: {
    name: 'Enable Resource Management',
    category: ApplicationSettingCategory.TRAFFICKING,
    hidden: true,
    order: 5,
  },
  /* #endregion */

  /* #region LOGIN */
  [ApplicationSetting.USE_SSO_LOGIN_ONLY]: {
    name: 'Use Sso Login Only',
    category: ApplicationSettingCategory.LOGIN,
    order: 0,
  },
  /* #endregion */

  /* #region ADOBESIGN */
  [ApplicationSetting.ADOBESIGN_OUTPUT_ON_DRAFT]: {
    name: 'Enable AdobeSign on Draft',
    category: ApplicationSettingCategory.ADOBESIGN,
    order: 0,
  },
  [ApplicationSetting.ADOBESIGN_OUTPUT_ON_REVIEW]: {
    name: 'Enable AdobeSign on In Review',
    category: ApplicationSettingCategory.ADOBESIGN,
    order: 1,
  },
  [ApplicationSetting.ADOBESIGN_OUTPUT_ON_AGREEMENT]: {
    name: 'Enable AdobeSign on In Approval',
    category: ApplicationSettingCategory.ADOBESIGN,
    order: 2,
  },
  [ApplicationSetting.ADOBESIGN_OUTPUT_ON_CLIENT_APPROVAL]: {
    name: 'Enable AdobeSign on In Client Approval',
    category: ApplicationSettingCategory.ADOBESIGN,
    order: 3,
  },
  [ApplicationSetting.ADOBESIGN_OUTPUT_ON_CLIENT_ACCEPTED]: {
    name: 'Enable AdobeSign on Client Accepted',
    category: ApplicationSettingCategory.ADOBESIGN,
    order: 4,
  },
  [ApplicationSetting.ADOBESIGN_OUTPUT_ON_CLOSED]: {
    name: 'Enable AdobeSign on Closed',
    category: ApplicationSettingCategory.ADOBESIGN,
    order: 5,
  },
  [ApplicationSetting.ADOBESIGN_OUTPUT_OPTIONAL_COMPANY_SIGNER]: {
    name: 'Enable AdobeSign optional company signer',
    category: ApplicationSettingCategory.ADOBESIGN,
    order: 6,
  },
  /* #endregion */

  /* #region SOW */
  [ApplicationSetting.SOW__GLOBAL]: {
    name: 'Enable {LANG:scope_of_work}',
    category: ApplicationSettingCategory.SOW,
    description:
      'Shows or hides {LANG:sow} tab on {LANG:scope} landing page. Allows users to group {LANG:scope.p} in a {LANG:sow}',
    order: 0,
  },
  [ApplicationSetting.SOW__RETAINED_HOURS]: {
    name: 'Enable {LANG:scope_of_work} Retained Hours',
    category: ApplicationSettingCategory.SOW,
    hidden: true,
    order: 1,
  },
  [ApplicationSetting.SOW__OWNER]: {
    name: '{LANG:scope_of_work} Owner',
    category: ApplicationSettingCategory.SOW,
    description: 'Allows to add an owner to the {LANG:sow}',
    order: 2,
  },
  /* #endregion */

  /* #region COMPANY */
  [ApplicationSetting.RATECARD__SCOPEMARK__RATES]: {
    name: 'Show {LANG:scope_mark} Ratecard Rates',
    category: ApplicationSettingCategory.COMPANY,
    description: 'Allows users to set up and display scope start and end date',
    hidden: true,
    order: 0,
  },
  [ApplicationSetting.RATECARD_LOCATION__ENABLE]: {
    name: 'Enable Ratecard Location',
    description: 'Adds a status to the Approval flow where the client approves the scope',
    category: ApplicationSettingCategory.COMPANY,
    order: 1,
  },
  [ApplicationSetting.RATECARD__SCOPEMARK__HIDE]: {
    name: 'Hide {LANG:scope_mark} Ratecard',
    category: ApplicationSettingCategory.COMPANY,
    description: 'Hide ScopeMark ratecard	Shows or hides an out of the box (demo) ratecard from the system',
    isConfigurable: true,
    config: (user) => isNotLegalCompanyTypeOrEnabled(user.company, ApplicationSetting.RATECARD__SCOPEMARK__HIDE),
    order: 2,
  },
  [ApplicationSetting.FEE_ITEM__ENABLE]: {
    name: 'Enable Line Items fees',
    description:
      'Allows to add fees on granular level. Should be turn on for the users to select settings for Stage, {LANG:deliverable} and {LANG:component} Fees',
    category: ApplicationSettingCategory.COMPANY,
    order: 3,
  },
  [ApplicationSetting.SUPPORT_BUTTON__ENABLE]: {
    name: 'Enable Support button',
    description:
      'Adds or removes the "Get in touch" button for raising support tickets in top right corner of the screen',
    category: ApplicationSettingCategory.COMPANY,
    order: 4,
  },
  [ApplicationSetting.COMPANY_CHILD_ACCOUNT_ACCESS_RULES]: {
    name: 'Enable Child Account Access Management',
    category: ApplicationSettingCategory.COMPANY,
    hidden: true,
    order: 5,
  },
  [ApplicationSetting.RATECARD__IMPORT_EXPORT]: {
    name: 'Enable Ratecard Import/Export',
    category: ApplicationSettingCategory.COMPANY,
    hidden: true,
    order: 6,
  },
  /* #endregion */

  /* #region GLOBAL */
  [ApplicationSetting.GROUPS__GLOBAL]: {
    name: 'Enable groups',
    hidden: true,
    category: ApplicationSettingCategory.GLOBAL,
    order: 0,
  },
  [ApplicationSetting.SAML__GLOBAL]: {
    name: 'Enable SAML',
    hidden: true,
    category: ApplicationSettingCategory.GLOBAL,
    order: 1,
  },
  [ApplicationSetting.CAPTCHA__ENABLE]: {
    name: 'Enable Captcha',
    hidden: true,
    category: ApplicationSettingCategory.GLOBAL,
    order: 2,
  },
  [ApplicationSetting.CURRENCY_CONVERSION]: {
    name: 'Currency Conversion',
    category: ApplicationSettingCategory.GLOBAL,
    hidden: true,
    order: 3,
  },
  /* #endregion */
}

const isNotLegalCompanyTypeOrEnabled = (company, setting) =>
  !(company.companyType === 'COMPANY' || company.companyType === 'LEGAL') || company.hasApplicationSetting(setting)
